import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  useEmailTemplatesQuery,
  useCreateEmailTemplateMutation,
  useUpdateEmailTemplateMutation,
  useDeleteEmailTemplateMutation,
  useSendEmailTemplateNowMutation,
  useEmailTemplateSendNowStatusQuery,
  useEmailTemplateSendsQuery,
  type EmailTemplate,
  type EmailSend,
} from "../api/queries/emailTemplateQueries";
import { formatAppDateTime } from "../utils/date";
import {
  Button,
  Card,
  Group,
  Stack,
  Text,
  ActionIcon,
  Badge,
  Table,
  Loader,
  Tooltip,
} from "@mantine/core";
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconMail,
  IconClock,
  IconCheck,
  IconX,
  IconCopy,
  IconPlayerPlay,
} from "@tabler/icons-react";
import { toast } from "sonner";
import {
  EmailTemplateModal,
  type TemplateFormData,
} from "./EmailTemplateModal";
import { RecurringTemplateSendNowPreviewModal } from "./RecurringTemplateSendNowPreviewModal";
import {
  DEFAULT_INVOICE_LIST_FORMAT,
  STARTER_TEMPLATE_BODY,
  STARTER_TEMPLATE_SUBJECT,
  STARTER_SINGLE_TEMPLATE_BODY,
  STARTER_SINGLE_TEMPLATE_SUBJECT,
} from "../utils/emailTemplatePreview";

/** Hebrew Unicode ranges. */
function containsHebrew(text: string): boolean {
  if (!text) return false;
  return /[\u0590-\u05FF\uFB1D-\uFDFD\uFE70-\uFEFC]/.test(text);
}

const initialFormData: TemplateFormData = {
  name: "",
  subject: "",
  body: "",
  trigger_type: "DUE_DATE",
  offset_days: 0,
  condition_balance: "ANY",
  audience_type: "MULTIPLE_INVOICES",
  invoice_list_format: DEFAULT_INVOICE_LIST_FORMAT,
  enabled: true,
  group_ids: [],
  repeat_interval_days: 5,
  invoice_inclusion_scope: "OVERDUE_OPEN",
  inclusion_min_days_overdue: 90,
};

export function EmailTemplatesManager() {
  const { data: templates, isLoading } = useEmailTemplatesQuery();
  const createMutation = useCreateEmailTemplateMutation();
  const updateMutation = useUpdateEmailTemplateMutation();
  const deleteMutation = useDeleteEmailTemplateMutation();
  const sendNowMutation = useSendEmailTemplateNowMutation();
  const [pendingSend, setPendingSend] = useState<{
    templateId: number;
    jobId: string;
  } | null>(null);

  const sendNowStatusQuery = useEmailTemplateSendNowStatusQuery(
    pendingSend?.templateId ?? 0,
    pendingSend?.jobId ?? null,
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(
    null,
  );
  const [formData, setFormData] = useState<TemplateFormData>(initialFormData);
  const [viewingSends, setViewingSends] = useState<number | null>(null);
  const [sendNowPreviewTemplate, setSendNowPreviewTemplate] =
    useState<EmailTemplate | null>(null);

  const handleOpenCreate = () => {
    setEditingTemplate(null);
    setFormData(initialFormData);
    setModalOpen(true);
  };

  const handleOpenStarterTemplate = (single = false) => {
    setEditingTemplate(null);
    setFormData({
      ...initialFormData,
      name: single ? "Single invoice reminder" : "Payment reminder",
      subject: single
        ? STARTER_SINGLE_TEMPLATE_SUBJECT
        : STARTER_TEMPLATE_SUBJECT,
      body: single ? STARTER_SINGLE_TEMPLATE_BODY : STARTER_TEMPLATE_BODY,
      audience_type: single ? "SINGLE_INVOICE" : "MULTIPLE_INVOICES",
      invoice_list_format: DEFAULT_INVOICE_LIST_FORMAT,
      offset_days: 7,
    });
    setModalOpen(true);
  };

  const handleOpenEdit = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      body: template.body,
      trigger_type: template.trigger_type,
      offset_days: template.offset_days,
      condition_balance: template.condition_balance,
      audience_type: template.audience_type ?? "MULTIPLE_INVOICES",
      invoice_list_format:
        template.invoice_list_format ?? DEFAULT_INVOICE_LIST_FORMAT,
      enabled: template.enabled,
      group_ids: template.group_ids ?? [],
      repeat_interval_days: template.repeat_interval_days ?? 5,
      invoice_inclusion_scope:
        template.invoice_inclusion_scope ?? "OVERDUE_OPEN",
      inclusion_min_days_overdue: template.inclusion_min_days_overdue ?? 90,
    });
    setModalOpen(true);
  };

  const handleClone = (template: EmailTemplate) => {
    setEditingTemplate(null);
    setFormData({
      name: `Copy of ${template.name}`,
      subject: template.subject,
      body: template.body,
      trigger_type: template.trigger_type,
      offset_days: template.offset_days,
      condition_balance: template.condition_balance,
      audience_type: template.audience_type ?? "MULTIPLE_INVOICES",
      invoice_list_format:
        template.invoice_list_format ?? DEFAULT_INVOICE_LIST_FORMAT,
      enabled: false,
      group_ids: template.group_ids ?? [],
      repeat_interval_days: template.repeat_interval_days ?? 5,
      invoice_inclusion_scope:
        template.invoice_inclusion_scope ?? "OVERDUE_OPEN",
      inclusion_min_days_overdue: template.inclusion_min_days_overdue ?? 90,
    });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingTemplate(null);
    setFormData(initialFormData);
  };

  const handleSubmit = () => {
    if (!formData.name || !formData.subject || !formData.body) {
      toast.error("Please fill in all required fields");
      return;
    }

    const templateData = {
      ...formData,
      invoice_list_format:
        formData.audience_type === "SINGLE_INVOICE"
          ? null
          : formData.invoice_list_format || DEFAULT_INVOICE_LIST_FORMAT,
      repeat_interval_days:
        formData.trigger_type === "OVERDUE_RECURRING"
          ? formData.repeat_interval_days
          : null,
      invoice_inclusion_scope:
        formData.trigger_type === "OVERDUE_RECURRING"
          ? formData.invoice_inclusion_scope
          : null,
      inclusion_min_days_overdue:
        formData.trigger_type === "OVERDUE_RECURRING" &&
        formData.invoice_inclusion_scope === "OVERDUE_SINCE"
          ? formData.inclusion_min_days_overdue
          : null,
    };

    if (editingTemplate) {
      updateMutation.mutate(
        {
          templateId: editingTemplate.id,
          templateData,
        },
        {
          onSuccess: () => {
            handleCloseModal();
          },
        },
      );
    } else {
      createMutation.mutate(templateData, {
        onSuccess: () => {
          handleCloseModal();
        },
      });
    }
  };

  const handleDelete = (templateId: number) => {
    if (confirm("Are you sure you want to delete this template?")) {
      deleteMutation.mutate(templateId);
    }
  };

  const handleToggleEnabled = (template: EmailTemplate) => {
    updateMutation.mutate({
      templateId: template.id,
      templateData: { enabled: !template.enabled },
    });
  };

  const handleOpenSendNowPreview = (template: EmailTemplate) => {
    setSendNowPreviewTemplate(template);
  };

  const handleCloseSendNowPreview = () => {
    setSendNowPreviewTemplate(null);
  };

  const handleConfirmSendNow = () => {
    if (!sendNowPreviewTemplate) return;
    sendNowMutation.mutate(sendNowPreviewTemplate.id, {
      onSuccess: (data) => {
        handleCloseSendNowPreview();
        if (data.status === "already_running") {
          toast.info("Send already running", {
            description:
              data.message ??
              "A send is already in progress for this template.",
          });
          return;
        }
        if (data.status === "backpressure") {
          toast.error("Queue is busy", {
            description:
              data.message ?? "Please try again in a few moments.",
          });
          return;
        }
        if (data.job_id) {
          setPendingSend({
            templateId: sendNowPreviewTemplate.id,
            jobId: data.job_id,
          });
          toast.info("Send started", {
            description: "Template send is running in the background.",
          });
          return;
        }
        toast.info(data.message ?? "Send requested");
      },
    });
  };

  useEffect(() => {
    const status = sendNowStatusQuery.data?.status;
    if (!pendingSend || !status) return;
    if (status !== "completed" && status !== "failed") return;

    const stats = sendNowStatusQuery.data?.stats;
    if (status === "completed") {
      toast.success(
        `Send complete: ${stats?.emails_sent ?? 0} sent, ${stats?.emails_failed ?? 0} failed`,
      );
    } else {
      toast.error("Send failed", {
        description:
          sendNowStatusQuery.data?.error_message ??
          "The template send did not complete.",
      });
    }
    setPendingSend(null);
  }, [pendingSend, sendNowStatusQuery.data]);

  const formatTriggerSummary = (template: EmailTemplate) => {
    if (template.trigger_type === "OVERDUE_RECURRING") {
      const interval =
        template.repeat_interval_days === 0
          ? "once only"
          : `every ${template.repeat_interval_days} business days`;
      const scope =
        template.invoice_inclusion_scope === "ALL_OPEN"
          ? "all open"
          : template.invoice_inclusion_scope === "OVERDUE_SINCE"
            ? `overdue ≥${template.inclusion_min_days_overdue ?? 0}d`
            : "overdue open";
      return `Overdue recurring — min ${template.offset_days}d overdue, ${interval}, includes ${scope}`;
    }
    return `${template.trigger_type === "INVOICE_DATE" ? "Invoice Date" : "Due Date"}${
      template.offset_days !== 0
        ? ` ${template.offset_days > 0 ? "+" : ""}${template.offset_days} days`
        : ""
    }`;
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <div>
          <Text fw={600} size="lg">
            Email Templates
          </Text>
          <Stack gap={2} mt={2}>
            <Text size="sm" c="dimmed">
              Create templates to automatically send emails based on invoice
              events. Emails are grouped by customer, sending one email per
              customer.
            </Text>
            <Text size="xs" c="dimmed">
              Use variables like {"{full_name}"} and {"{invoice_list}"} to
              personalize emails. Invoice PDFs attach automatically when
              available.
            </Text>
          </Stack>
        </div>
        <Button
          leftSection={<IconPlus size={18} />}
          onClick={handleOpenCreate}
          variant="filled"
          color="indigo"
        >
          Create Template
        </Button>
      </Group>

      {templates && templates.length === 0 ? (
        <Card withBorder p="xl" className="text-center">
          <Text c="dimmed" mb="md">
            No email templates yet. Start from our example or create your own.
          </Text>
          <Group justify="center" gap="sm">
            <Button
              onClick={() => handleOpenStarterTemplate(false)}
              variant="filled"
              color="indigo"
            >
              Multi-invoice starter
            </Button>
            <Button
              onClick={() => handleOpenStarterTemplate(true)}
              variant="light"
              color="indigo"
            >
              Single-invoice starter
            </Button>
            <Button onClick={handleOpenCreate} variant="light" color="indigo">
              Blank template
            </Button>
          </Group>
        </Card>
      ) : (
        <Stack gap="sm">
          {templates?.map((template) => (
            <Card key={template.id} withBorder p="md">
              <Group justify="space-between" align="flex-start">
                <Stack gap="xs" style={{ flex: 1 }}>
                  <Group gap="sm" align="center">
                    <Text
                      fw={500}
                      dir={containsHebrew(template.name) ? "rtl" : "ltr"}
                    >
                      {template.name}
                    </Text>
                    <Badge
                      color={template.enabled ? "green" : "gray"}
                      variant="light"
                      size="sm"
                    >
                      {template.enabled ? "Auto-send on" : "Auto-send off"}
                    </Badge>
                    <Badge color="indigo" variant="light" size="sm">
                      {template.audience_type === "SINGLE_INVOICE"
                        ? "Single invoice"
                        : "Multiple invoices"}
                    </Badge>
                    {template.trigger_type === "OVERDUE_RECURRING" ? (
                      <Badge color="orange" variant="light" size="sm">
                        Recurring
                      </Badge>
                    ) : null}
                    {(template.groups?.length ?? 0) > 0 ? (
                      template.groups.map((group) => (
                        <Badge
                          key={group.id}
                          color="violet"
                          variant="light"
                          size="sm"
                        >
                          {group.name}
                        </Badge>
                      ))
                    ) : (
                      <Badge color="gray" variant="light" size="sm">
                        Default
                      </Badge>
                    )}
                  </Group>
                  <Text size="sm" c="dimmed">
                    Subject:{" "}
                    <span
                      dir={containsHebrew(template.subject) ? "rtl" : "ltr"}
                      style={{
                        unicodeBidi: "isolate",
                      }}
                    >
                      {template.subject}
                    </span>
                  </Text>
                  <Group gap="md" mt="xs">
                    <Group gap={4}>
                      <IconClock size={14} />
                      <Text size="xs" c="dimmed">
                        {formatTriggerSummary(template)}
                      </Text>
                    </Group>
                    <Text size="xs" c="dimmed">
                      Balance:{" "}
                      {template.condition_balance === "GT_0"
                        ? "Greater than 0"
                        : "Any"}
                    </Text>
                  </Group>
                </Stack>
                <Group gap="xs">
                  {template.trigger_type === "OVERDUE_RECURRING" ? (
                    <Tooltip label="Send now">
                      <ActionIcon
                        variant="subtle"
                        color="teal"
                        onClick={() => handleOpenSendNowPreview(template)}
                        loading={
                          sendNowMutation.isPending ||
                          (pendingSend?.templateId === template.id &&
                            sendNowStatusQuery.isFetching)
                        }
                      >
                        <IconPlayerPlay size={18} />
                      </ActionIcon>
                    </Tooltip>
                  ) : null}
                  <Tooltip label="View send history">
                    <ActionIcon
                      variant="subtle"
                      color="blue"
                      onClick={() =>
                        setViewingSends(
                          viewingSends === template.id ? null : template.id,
                        )
                      }
                    >
                      <IconMail size={18} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Toggle auto-send">
                    <ActionIcon
                      variant="subtle"
                      color={template.enabled ? "orange" : "green"}
                      onClick={() => handleToggleEnabled(template)}
                      loading={updateMutation.isPending}
                    >
                      {template.enabled ? (
                        <IconX size={18} />
                      ) : (
                        <IconCheck size={18} />
                      )}
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Clone">
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      onClick={() => handleClone(template)}
                    >
                      <IconCopy size={18} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Edit">
                    <ActionIcon
                      variant="subtle"
                      color="blue"
                      onClick={() => handleOpenEdit(template)}
                    >
                      <IconEdit size={18} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Delete">
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => handleDelete(template.id)}
                      loading={deleteMutation.isPending}
                    >
                      <IconTrash size={18} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Group>
              {viewingSends === template.id && (
                <TemplateSendsHistory templateId={template.id} />
              )}
            </Card>
          ))}
        </Stack>
      )}

      <RecurringTemplateSendNowPreviewModal
        opened={!!sendNowPreviewTemplate}
        template={sendNowPreviewTemplate}
        isSending={sendNowMutation.isPending}
        onClose={handleCloseSendNowPreview}
        onConfirmSend={handleConfirmSendNow}
      />

      <EmailTemplateModal
        opened={modalOpen}
        onClose={handleCloseModal}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleSubmit}
        isEditing={!!editingTemplate}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />
    </Stack>
  );
}

function groupEmailSends(sends: EmailSend[]): EmailSend[] {
  const grouped: EmailSend[] = [];
  const seenMessageIds = new Set<string>();

  for (const send of sends) {
    if (send.gmail_message_id) {
      if (seenMessageIds.has(send.gmail_message_id)) {
        continue;
      }
      seenMessageIds.add(send.gmail_message_id);
    }
    grouped.push(send);
  }

  return grouped;
}

function sendStatusColor(status: EmailSend["status"]) {
  if (status === "sent") return "green";
  if (status === "skipped") return "gray";
  return "red";
}

function buildEmailDeepLink(send: EmailSend): string | null {
  if (!send.gmail_thread_id) return null;

  const invoiceId =
    send.invoice_external_ids?.[0] ?? send.payment_external_id ?? null;
  if (!invoiceId) return null;

  const params = new URLSearchParams({ thread: send.gmail_thread_id });
  if (send.gmail_message_id) {
    params.set("message", send.gmail_message_id);
  }
  return `/invoice/${invoiceId}?${params.toString()}`;
}

function TemplateSendsHistory({ templateId }: { templateId: number }) {
  const { data: sends, isLoading } = useEmailTemplateSendsQuery(templateId);

  const groupedSends = useMemo(
    () => (sends ? groupEmailSends(sends) : []),
    [sends],
  );

  if (isLoading) {
    return <Loader size="sm" mt="md" />;
  }

  if (!groupedSends.length) {
    return (
      <Text size="sm" c="dimmed" mt="md">
        No emails sent yet for this template.
      </Text>
    );
  }

  return (
    <div style={{ marginTop: "1rem" }}>
      <Text size="sm" fw={500} mb="xs">
        Send History ({groupedSends.length})
      </Text>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Sent At</Table.Th>
            <Table.Th>Recipient</Table.Th>
            <Table.Th>Invoices</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Error</Table.Th>
            <Table.Th>View email</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {groupedSends.map((send) => {
            const emailLink = buildEmailDeepLink(send);
            return (
              <Table.Tr key={send.gmail_message_id ?? send.id}>
                <Table.Td>{formatAppDateTime(send.sent_at)}</Table.Td>
                <Table.Td>
                  {send.recipient_emails?.length ? (
                    <Text size="xs">{send.recipient_emails.join(", ")}</Text>
                  ) : (
                    <Text size="xs" c="dimmed">
                      -
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  {send.invoice_external_ids?.length ? (
                    <div className="flex flex-wrap gap-1">
                      {send.invoice_external_ids.map((invoiceId) => (
                        <Link
                          key={invoiceId}
                          to={`/invoice/${invoiceId}`}
                          className="text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:underline"
                        >
                          {invoiceId}
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <Text size="xs" c="dimmed">
                      -
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Badge
                    color={sendStatusColor(send.status)}
                    variant="light"
                    size="sm"
                  >
                    {send.status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  {send.error_message ? (
                    <Text size="xs" c="red">
                      {send.error_message}
                    </Text>
                  ) : (
                    <Text size="xs" c="dimmed">
                      -
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  {emailLink ? (
                    <Link
                      to={emailLink}
                      className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      View email
                    </Link>
                  ) : (
                    <Text size="xs" c="dimmed">
                      -
                    </Text>
                  )}
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </div>
  );
}
